import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { DialogFooter } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  type OutboundCallAutomation,
  useCreateAutomation,
  useFilterPreview,
} from '@/models/outboundCallAutomation'
import { buildSalesLeadParams, type SalesLeadFilters } from '@/models/salesLead'
import { type SequenceStepInput } from '@/models/sequence'
import { SequenceStepsBuilder } from './Sequence/SequenceStepsBuilder'
import { SequenceTemplateSelect } from './Sequence/SequenceTemplateSelect'

interface OutboundCallAutomationCreateProps {
  /** Filters for a normal run; omitted in follow-up mode. */
  filters?: SalesLeadFilters
  /** Set for a follow-up run — re-dials this source run's CALLED leads (INS-253). */
  followUpOf?: OutboundCallAutomation
  onDone: () => void
}

export function OutboundCallAutomationCreate({
  filters,
  followUpOf,
  onDone,
}: OutboundCallAutomationCreateProps) {
  const isFollowUp = !!followUpOf
  const [name, setName] = useState('')
  const [limit, setLimit] = useState('')
  const [pauseAfter, setPauseAfter] = useState('')
  const [pauseDuration, setPauseDuration] = useState('')
  const [steps, setSteps] = useState<SequenceStepInput[]>([])
  // Bumped to remount the builder when a template replaces its steps.
  const [builderKey, setBuilderKey] = useState(0)
  const [pendingAction, setPendingAction] = useState<'save' | 'start' | null>(null)
  // Filter preview only applies to a normal (filter-based) run.
  const { data: preview } = useFilterPreview(filters ?? {}, !isFollowUp)
  const create = useCreateAutomation()

  // Only a positive integer caps the run; "", "0", or junk mean "no cap" — kept
  // consistent between what we submit and what the preview shows (backend rejects <1).
  const parsedLimit = Number(limit) >= 1 ? Math.floor(Number(limit)) : null

  // Auto-pause: both empty = no auto-pause; both required together.
  const parsedPauseAfter = Number(pauseAfter) >= 1 ? Math.floor(Number(pauseAfter)) : null
  const parsedPauseDuration = Number(pauseDuration) >= 1 ? Math.floor(Number(pauseDuration)) : null
  const pauseIncomplete = (parsedPauseAfter === null) !== (parsedPauseDuration === null)

  async function submit(start: boolean) {
    setPendingAction(start ? 'start' : 'save')
    try {
      await create.mutateAsync({
        name,
        leadFilters: isFollowUp ? {} : buildSalesLeadParams(filters ?? {}),
        limit: parsedLimit,
        pauseAfterLeads: parsedPauseAfter,
        pauseDurationSeconds: parsedPauseDuration,
        followUpOf: followUpOf?.id ?? null,
        sequenceSteps: steps,
        start,
      })
      toast.success(start ? 'Automation created and started' : 'Automation saved')
      onDone()
    } catch {
      toast.error('Failed to save automation')
    } finally {
      setPendingAction(null)
    }
  }

  const matchCount = followUpOf ? followUpOf.followUpEligibleCount : (preview?.count ?? null)
  const effectiveCount = matchCount !== null && parsedLimit ? Math.min(matchCount, parsedLimit) : matchCount
  const capped = matchCount !== null && parsedLimit !== null && matchCount > parsedLimit

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <div>
        {followUpOf ? (
          <Label className="mb-1.5 block">
            Follow-up of “{followUpOf.name || 'Automation'}” — re-dials the leads its calls didn't reach
          </Label>
        ) : (
          <>
            <Label className="mb-1.5 block">Leads matching the current filters</Label>
            <ul className="text-muted-foreground list-inside list-disc text-sm">
              {(preview?.summary ?? []).map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </>
        )}
        <div className="mt-4 text-sm">
          <span className="font-semibold">{effectiveCount ?? '…'}</span>{' '}
          {isFollowUp ? 'leads will be re-dialed' : 'leads will be added'}
          {capped && (
            <span className="text-muted-foreground">
              {' '}
              (of {matchCount} matching, capped at {parsedLimit})
            </span>
          )}
          .
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <Label>Name (optional)</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. TX blitz" />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label>Limit</Label>
          <Input
            type="number"
            min={1}
            value={limit}
            onChange={(e) => setLimit(e.target.value)}
            placeholder="No cap"
          />
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label>Auto-pause (optional)</Label>
        <div className="text-muted-foreground flex flex-wrap items-center gap-2 text-sm">
          Pause after
          <Input
            type="number"
            min={1}
            value={pauseAfter}
            onChange={(e) => setPauseAfter(e.target.value)}
            placeholder="10"
            className="h-8 w-20"
          />
          leads, for
          <Input
            type="number"
            min={1}
            value={pauseDuration}
            onChange={(e) => setPauseDuration(e.target.value)}
            placeholder="60"
            className="h-8 w-20"
          />
          seconds.
        </div>
        {pauseIncomplete && (
          <span className="text-xs text-red-600">Set both fields, or leave both empty.</span>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <Label>Sequence (runs after missed calls)</Label>
        <SequenceTemplateSelect
          confirmReplace={steps.length > 0}
          onSelect={(template) => {
            setSteps(template.steps)
            setBuilderKey((key) => key + 1)
          }}
        />
        <SequenceStepsBuilder
          key={builderKey}
          initialSteps={steps}
          onChange={setSteps}
          emptyText="No sequence — leads are just dialed, no follow-up."
        />
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onDone} disabled={create.isPending}>
          Cancel
        </Button>
        <Button
          variant="outline"
          onClick={() => submit(false)}
          disabled={create.isPending || pauseIncomplete}
          loading={pendingAction === 'save' ? 'Saving…' : undefined}
        >
          Save
        </Button>
        <Button
          onClick={() => submit(true)}
          disabled={create.isPending || pauseIncomplete}
          loading={pendingAction === 'start' ? 'Starting…' : undefined}
        >
          Save and start
        </Button>
      </DialogFooter>
    </div>
  )
}
